import type {OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import {OttopilotColumnType} from "~/components/ottopilotDataViewer/column";
import {type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {
    AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID,
    getAmaronChildDispositionColumnIds,
    getAmaronChildDispositionLevelKeys,
    getAmaronDispositionIdsForLevels,
    getAmaronParentDispositionLevelsForFilterLevel,
    getAmaronUniqueDispositionLevelValues,
    type AmaronDispositionLevelKey,
} from "~/domainModels/workspaceSpecific/amaron";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {flatDispositionToDisplayNameFormat} from "~/utilities/utilities";

export const AMARON_EVENT_CALLS_DISPOSITION_LEVEL_COLUMN_IDS: ReadonlyArray<string> = [
    AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
];

export const AMARON_JOURNEY_DISPOSITION_LEVEL_BY_COLUMN_ID: ReadonlyMap<string, AmaronDispositionLevelKey> = new Map([
    [AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID, "l1"],
    [AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID, "l2"],
    [AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID, "l3"],
    [AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID, "l4"],
]);

export const AMARON_EVENT_CALLS_DISPOSITION_LEVEL_BY_COLUMN_ID: ReadonlyMap<string, AmaronDispositionLevelKey> =
    new Map([
        [AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID, "l1"],
        [AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID, "l2"],
        [AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID, "l3"],
        [AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID, "l4"],
    ]);

export const AMARON_JOURNEY_DISPOSITION_LEVEL_LABEL_BY_COLUMN_ID: ReadonlyMap<string, string> = new Map([
    [AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID, "L1 Disposition"],
    [AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID, "L2 Disposition"],
    [AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID, "L3 Disposition"],
    [AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID, "L4 Disposition"],
]);

export const AMARON_EVENT_CALLS_DISPOSITION_LEVEL_LABEL_BY_COLUMN_ID: ReadonlyMap<string, string> = new Map([
    [AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID, "L1 Disposition"],
    [AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID, "L2 Disposition"],
    [AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID, "L3 Disposition"],
    [AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID, "L4 Disposition"],
]);

export type AmaronDispositionFilterScope = "journey" | "eventCalls";

export function isAmaronJourneyDispositionUiFilterColumn(columnId: string): boolean {
    return AMARON_JOURNEY_DISPOSITION_LEVEL_BY_COLUMN_ID.has(columnId);
}

export function hasAmaronDispositionUiLevelFilters(
    levels: ReturnType<typeof readAmaronDispositionLevelsFromFilters>,
): boolean {
    return (
        levels.l1Values !== undefined ||
        levels.l2Values !== undefined ||
        levels.l3Values !== undefined ||
        levels.l4Values !== undefined
    );
}

/** Adds API `disposition` in-filter from L1–L4 UI line items when missing (e.g. persisted UI-only filters). */
export function appendAmaronJourneyDispositionApiFilterIfMissing(
    backendLineItems: FilterConfiguration["lineItems"],
    sourceLineItems: FilterConfiguration["lineItems"],
): FilterConfiguration["lineItems"] {
    return appendAmaronDispositionApiFilterIfMissing(backendLineItems, sourceLineItems, "journey");
}

function appendAmaronDispositionApiFilterIfMissing(
    backendLineItems: FilterConfiguration["lineItems"],
    sourceLineItems: FilterConfiguration["lineItems"],
    scope: AmaronDispositionFilterScope,
): FilterConfiguration["lineItems"] {
    const apiDispositionColumnId = getAmaronDispositionApiColumnId(scope);
    const hasApiDispositionFilter = backendLineItems.some(
        (lineItem) =>
            lineItem.columnId === apiDispositionColumnId && lineItem.operation === FilterOperation.in,
    );
    if (hasApiDispositionFilter === true) {
        return backendLineItems;
    }

    const activeLevels = readAmaronDispositionLevelsFromFilters(sourceLineItems, scope);
    if (hasAmaronDispositionUiLevelFilters(activeLevels) !== true) {
        return backendLineItems;
    }

    const dispositionIds = getAmaronDispositionIdsForLevels(activeLevels).map((id) => String(id));
    if (dispositionIds.length === 0) {
        return backendLineItems;
    }

    return [
        ...backendLineItems,
        {
            columnId: apiDispositionColumnId,
            operation: FilterOperation.in,
            values: dispositionIds,
        },
    ];
}

/**
 * Export-safe filters: drop client-only L1–L4 columns and fold them into the physical
 * disposition / LAST_DISPOSITION API filter. Avoids Pluto "Invalid … column" on export.
 */
export function sanitizeAmaronDispositionFiltersForExport(
    filterConfiguration: FilterConfiguration,
    scope: AmaronDispositionFilterScope,
): FilterConfiguration {
    const sourceLineItems = filterConfiguration.lineItems;
    const withoutUiLevels = sourceLineItems.filter((lineItem) => {
        if (scope === "journey") {
            return isAmaronJourneyDispositionUiFilterColumn(lineItem.columnId) !== true;
        }
        return isAmaronEventCallsDispositionUiFilterColumn(lineItem.columnId) !== true;
    });
    return {
        ...filterConfiguration,
        lineItems: appendAmaronDispositionApiFilterIfMissing(withoutUiLevels, sourceLineItems, scope),
    };
}

export function isAmaronEventCallsDispositionUiFilterColumn(columnId: string): boolean {
    return AMARON_EVENT_CALLS_DISPOSITION_LEVEL_BY_COLUMN_ID.has(columnId);
}

export function getAmaronDispositionApiColumnId(scope: AmaronDispositionFilterScope): string {
    return scope === "journey" ? "disposition" : AMARON__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID;
}

export function getAmaronDispositionLevelByColumnId(
    columnId: string,
    scope: AmaronDispositionFilterScope,
): AmaronDispositionLevelKey | undefined {
    if (scope === "journey") {
        return AMARON_JOURNEY_DISPOSITION_LEVEL_BY_COLUMN_ID.get(columnId);
    }
    return AMARON_EVENT_CALLS_DISPOSITION_LEVEL_BY_COLUMN_ID.get(columnId);
}

export function getAmaronDispositionLevelLabel(columnId: string, scope: AmaronDispositionFilterScope): string {
    const labelByColumnId =
        scope === "journey"
            ? AMARON_JOURNEY_DISPOSITION_LEVEL_LABEL_BY_COLUMN_ID
            : AMARON_EVENT_CALLS_DISPOSITION_LEVEL_LABEL_BY_COLUMN_ID;
    return labelByColumnId.get(columnId) ?? columnId;
}

export function getAmaronChildDispositionColumnIdsForScope(
    level: AmaronDispositionLevelKey,
    scope: AmaronDispositionFilterScope,
): Array<string> {
    if (scope === "journey") {
        return getAmaronChildDispositionColumnIds(level);
    }
    return getAmaronChildDispositionLevelKeys(level).map((childLevel) => {
        switch (childLevel) {
            case "l1":
                return AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID;
            case "l2":
                return AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID;
            case "l3":
                return AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID;
            case "l4":
                return AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID;
        }
    });
}

function readInFilterValues(
    lineItems: FilterConfiguration["lineItems"],
    columnId: string,
): Array<string> | undefined {
    const lineItem = lineItems.find(
        (item) => item.columnId === columnId && item.operation === FilterOperation.in,
    );
    if (lineItem === undefined || !("values" in lineItem) || lineItem.values === undefined) {
        return undefined;
    }
    return lineItem.values.length > 0 ? lineItem.values : undefined;
}

export function readAmaronDispositionLevelsFromFilters(
    lineItems: FilterConfiguration["lineItems"],
    scope: AmaronDispositionFilterScope,
): {
    l1Values?: Array<string>;
    l2Values?: Array<string>;
    l3Values?: Array<string>;
    l4Values?: Array<string>;
} {
    const levelByColumnId =
        scope === "journey"
            ? AMARON_JOURNEY_DISPOSITION_LEVEL_BY_COLUMN_ID
            : AMARON_EVENT_CALLS_DISPOSITION_LEVEL_BY_COLUMN_ID;
    const result: {
        l1Values?: Array<string>;
        l2Values?: Array<string>;
        l3Values?: Array<string>;
        l4Values?: Array<string>;
    } = {};
    for (const [columnId, level] of levelByColumnId.entries()) {
        const values = readInFilterValues(lineItems, columnId);
        if (values !== undefined) {
            switch (level) {
                case "l1":
                    result.l1Values = values;
                    break;
                case "l2":
                    result.l2Values = values;
                    break;
                case "l3":
                    result.l3Values = values;
                    break;
                case "l4":
                    result.l4Values = values;
                    break;
            }
        }
    }
    return result;
}

function removeDispositionFilterColumns(
    lineItems: FilterConfiguration["lineItems"],
    columnIdsToRemove: ReadonlySet<string>,
    apiDispositionColumnId: string,
): FilterConfiguration["lineItems"] {
    return lineItems.filter(
        (lineItem) =>
            lineItem.columnId !== apiDispositionColumnId && columnIdsToRemove.has(lineItem.columnId) !== true,
    );
}

export function buildAmaronDispositionFilterConfiguration(
    prev: FilterConfiguration,
    args: {
        scope: AmaronDispositionFilterScope;
        columnId: string;
        dispositionLevel: AmaronDispositionLevelKey;
        selectedItems: Array<string> | null;
    },
): FilterConfiguration {
    const apiDispositionColumnId = getAmaronDispositionApiColumnId(args.scope);
    const childColumnIds = getAmaronChildDispositionColumnIdsForScope(args.dispositionLevel, args.scope);
    const columnIdsToRemove = new Set<string>([args.columnId, ...childColumnIds]);
    const clearedLineItems = removeDispositionFilterColumns(
        prev.lineItems,
        columnIdsToRemove,
        apiDispositionColumnId,
    );

    if (args.selectedItems === null || args.selectedItems.length === 0) {
        const remainingLevels = readAmaronDispositionLevelsFromFilters(clearedLineItems, args.scope);
        const dispositionIds = getAmaronDispositionIdsForLevels(remainingLevels).map((id) => String(id));
        if (dispositionIds.length === 0) {
            return {
                revisionId: generateUuid(),
                lineItems: clearedLineItems,
            };
        }
        return {
            revisionId: generateUuid(),
            lineItems: [
                ...clearedLineItems,
                {
                    columnId: apiDispositionColumnId,
                    operation: FilterOperation.in,
                    values: dispositionIds,
                },
            ],
        };
    }

    const uiLineItems: FilterConfiguration["lineItems"] = [
        ...clearedLineItems,
        {
            columnId: args.columnId,
            operation: FilterOperation.in,
            values: args.selectedItems,
        },
    ];
    const activeLevels = readAmaronDispositionLevelsFromFilters(uiLineItems, args.scope);
    const dispositionIds = getAmaronDispositionIdsForLevels(activeLevels).map((id) => String(id));

    const lineItems: FilterConfiguration["lineItems"] =
        dispositionIds.length === 0
            ? uiLineItems
            : [
                  ...uiLineItems,
                  {
                      columnId: apiDispositionColumnId,
                      operation: FilterOperation.in,
                      values: dispositionIds,
                  },
              ];

    return {
        revisionId: generateUuid(),
        lineItems,
    };
}

export function buildAmaronDispositionFilterColumn<T>(args: {
    scope: AmaronDispositionFilterScope;
    columnId: string;
    dispositionLevel: AmaronDispositionLevelKey;
    valueGetter: (row: T) => string;
    sortingCapability?: boolean;
}): OttopilotColumn<T> {
    return {
        id: args.columnId,
        type: OttopilotColumnType.string,
        displayName: getAmaronDispositionLevelLabel(args.columnId, args.scope),
        valueGetter: args.valueGetter,
        valueFormatter: (value: string) => (value === "" ? "" : flatDispositionToDisplayNameFormat(value)),
        sortingCapability: args.sortingCapability ?? true,
        filteringCapability_contains: false,
        filteringCapability_in: {
            valuesQueryFn: async (context) => {
                const activeLevels = readAmaronDispositionLevelsFromFilters(
                    context?.filterConfiguration.lineItems ?? [],
                    args.scope,
                );
                const parentLevels = getAmaronParentDispositionLevelsForFilterLevel(
                    args.dispositionLevel,
                    {
                        l1: activeLevels.l1Values,
                        l2: activeLevels.l2Values,
                        l3: activeLevels.l3Values,
                        l4: activeLevels.l4Values,
                    },
                );
                return getAmaronUniqueDispositionLevelValues(
                    args.dispositionLevel,
                    Object.keys(parentLevels).length > 0 ? parentLevels : undefined,
                );
            },
        },
        customFilter: (filterArgs) => {
            const selectedItems = filterArgs.ephemeralValue.lineItems;
            filterArgs.controller.setFilterConfiguration((prev) =>
                buildAmaronDispositionFilterConfiguration(prev, {
                    scope: args.scope,
                    columnId: args.columnId,
                    dispositionLevel: args.dispositionLevel,
                    selectedItems,
                }),
            );
            filterArgs.closePopup();
        },
    } satisfies OttopilotColumn<T>;
}

export function isAmaronDispositionUiFilterColumn(columnId: string): boolean {
    return (
        isAmaronJourneyDispositionUiFilterColumn(columnId) === true
        || isAmaronEventCallsDispositionUiFilterColumn(columnId) === true
    );
}
